import React, { useState, useEffect } from "react";
import { Container, Stepper, Step, StepLabel, Box, Skeleton, Modal, Fade } from "@mui/material";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';

import RegistrationForm from "./registrationForm";
import OrderSummary from "./orderSummary";
import PaymentGateway from "./paymentGateway";
import SuccessScreen from "./successScreen";
import Footer from "../../components/footer";

import { saveFormData } from "../../redux/actions/UserActions";
import { getEvent } from "../../redux/actions/EventActions";
import { getAllPaymentTypes } from "../../redux/actions/PaymentTypeActions";

const steps = ["Details", "Summary", "Confirmation"];

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: "80%",
  maxWidth: 600,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const Registration = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const state = useSelector((state) => state);
  const localEventData = useSelector((state) => state.event.event);

  const [isLoading, setIsLoading] = useState(true);

  const getStepFromURL = () => {
    const step = parseInt(searchParams.get("step"), 10);
    return isNaN(step) || step < 0 || step >= steps.length ? 0 : step;
  };

  const [activeStep, setActiveStep] = useState(getStepFromURL);

  useEffect(() => {
    setActiveStep(getStepFromURL());
  }, [searchParams]);

  useEffect(() => {
    fetchEventData();
  }, []);

  const fetchEventData = async () => {
    if (!eventId) {
      navigate("/404");
      return;
    }

    try {
      await dispatch(getEvent(eventId));
    } catch (err) {
      // Optional: handle error
    } finally {
      setIsLoading(false);
    }
  };

  const [formData, setFormData] = useState(state.user.formData);
  const [selectedTicket, setSelectedTicket] = useState(formData?.selectedTicket);
  const [ticketOptions, setTicketOptions] = useState(localEventData.tickets || []);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPayment, setSelectedPayment] = useState(formData?.selectedPayment);
  const [registerStatus, setRegisterStatus] = useState('pending');
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const goToStep = (step) => {
    setSearchParams({ step });
  };

  useEffect(() => {
    fetchPaymentTypes();
  }, []);

  const fetchPaymentTypes = async () => {
    try {
      const response = await dispatch(getAllPaymentTypes());
      setPaymentMethods(response.data);
    } catch {
      fetchPaymentTypes();
    }
  };

  useEffect(() => {
    if (formData) {
      dispatch(saveFormData({ ...formData, selectedTicket, selectedPayment }));
    }
  }, [formData, selectedTicket, selectedPayment]);

  const scrollToTop = () => {
    const container = document.querySelector(".main-content");
    if (container) {
      container.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToTop();
  }, [activeStep]);

  const handleNext = () => goToStep(Math.min(activeStep + 1, steps.length - 1));
  const handleBack = () => goToStep(Math.max(activeStep - 1, 0));

  const renderStep = () => {
    if (isLoading) {
      return (
        <Box p={2}>
          <Skeleton variant="text" width="50%" height={40} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 1 }} />
          <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2, borderRadius: 1 }} />
          <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 3, borderRadius: 1 }} />

          <Skeleton variant="rectangular" width={120} height={40} sx={{ mt: 2, borderRadius: 2 }} />
        </Box>
      );
    }

    return (
      <>
        {activeStep === 0 && (
          <RegistrationForm
            registrationFields={localEventData.registrationFields}
            setActiveStep={handleNext}
            formData={formData}
            setFormData={setFormData}
          />
        )}

        {activeStep === 1 && (
          <OrderSummary
            setActiveStep={() => setIsPaymentModalOpen(true)}
            handleBack={handleBack}
            formData={formData}
            selectedTicket={selectedTicket}
            setSelectedTicket={setSelectedTicket}
            ticketOptions={ticketOptions}
            paymentMethods={paymentMethods}
            selectedPayment={selectedPayment}
            setSelectedPayment={setSelectedPayment}
          />
        )}

        <Modal open={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)}>
          <Box sx={modalStyle}>
            <PaymentGateway
              eventId={localEventData._id}
              formData={formData}
              setActiveStep={() => {
                setIsPaymentModalOpen(false);
                handleNext();
              }}
              handleBack={() => setIsPaymentModalOpen(false)}
              selectedPayment={selectedPayment}
              selectedTicket={selectedTicket}
              setRegisterStatus={setRegisterStatus}
            />
          </Box>
        </Modal>

        {activeStep === 2 && (
          <SuccessScreen formData={formData} status={registerStatus} />
        )}
      </>
    );
  };

  return (
    <>
      <Container maxWidth="md">
        <Box mt={4} mb={4}>
          <Box>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={index}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Box>
      </Container>

      <Fade in={!isLoading} timeout={500}>
        <div>{renderStep()}</div>
      </Fade>

      <Footer />
    </>
  );
};

export default Registration;
